/** Sell tab: seller terms gate → listings · upload wizard · sales & royalties. */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../api';
import { validatePreviewFile } from '../previewValidation';
import { LicenseVersion, SellerBeat, SellerMeta, SellerSale, UploadSlot, fmtCents, fmtPoints } from '../types';
import { BeatThumb, GhostBtn, PageShell, PrimaryBtn, cardPanel, fileDropProps, inputStyle, labelStyle, s, segOff, segOn } from '../ui';
import { ShopTabs } from '../components/ShopTabs';

type Tab = 'listings' | 'upload' | 'sales';

const STATUS_COLORS: Record<string, string> = {
  draft: 'var(--bs-text-muted)', processing: 'var(--bs-amber)', pending_review: 'var(--bs-blue-soft)', rejected: 'var(--bs-red)',
  listed: 'var(--bs-green)', reserved: 'var(--bs-amber)', sold: 'var(--bs-text)', delisted: 'var(--bs-text-muted)', takedown: 'var(--bs-red)',
};

const StatusChip: React.FC<{ status: string }> = ({ status }) => (
  <span style={{ ...s('padding:3px 9px;border-radius:6px;font-size:10.5px;font-weight:700;letter-spacing:0.06em;background:var(--bs-line)'), color: STATUS_COLORS[status] || 'var(--bs-text-muted)' }}>
    {status.replace('_', ' ').toUpperCase()}
  </span>
);

const UploadInProgressNotice: React.FC = () => (
  <div style={s('margin-top:14px;padding:10px 12px;border-radius:9px;background:var(--bs-amber-bg);color:var(--bs-amber);font-size:12.5px;line-height:1.45')}>
    Keep this page open until the upload and processing are complete. Refreshing, closing, or leaving this page may interrupt the upload and require you to select missing files again.
  </div>
);

const SellerPage: React.FC = () => {
  const navigate = useNavigate();
  const [accessChecked, setAccessChecked] = useState(false);
  const [canSell, setCanSell] = useState(false);
  const [meta, setMeta] = useState<SellerMeta | null>(null);
  const [tab, setTab] = useState<Tab>('listings');
  const [sellerTerms, setSellerTerms] = useState<LicenseVersion | null>(null);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [beats, setBeats] = useState<SellerBeat[] | null>(null);
  const [sales, setSales] = useState<{ sales: SellerSale[]; totals: any } | null>(null);
  const [royalties, setRoyalties] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadMeta = useCallback(() => api.getSellerMeta().then(setMeta).catch((e) => setError(e.message)), []);
  const loadBeats = useCallback(() => api.getSellerBeats().then((r) => setBeats(r.beats)).catch(() => setBeats([])), []);

  useEffect(() => {
    let cancelled = false;
    api.getSellAccess()
      .then((r) => {
        if (cancelled) return;
        const allowed = !!(r.allowed || r.has_access);
        setCanSell(allowed);
        setAccessChecked(true);
        if (!allowed) navigate('/store/beat-shop', { replace: true });
      })
      .catch(() => {
        if (cancelled) return;
        setCanSell(false);
        setAccessChecked(true);
        navigate('/store/beat-shop', { replace: true });
      });
    return () => { cancelled = true; };
  }, [navigate]);

  useEffect(() => {
    if (!accessChecked || !canSell) return;
    loadMeta();
    loadBeats();
  }, [accessChecked, canSell, loadMeta, loadBeats]);

  useEffect(() => {
    if (tab === 'sales') {
      api.getSellerSales().then(setSales).catch(() => setSales(null));
      api.getSellerRoyalties().then((r) => setRoyalties(r.royalties)).catch(() => setRoyalties([]));
    }
  }, [tab]);
  useEffect(() => {
    if (meta && !meta.seller_terms_accepted) {
      api.getCurrentLicense('seller').then(setSellerTerms).catch(() => {});
    }
  }, [meta]);

  const acceptTerms = async () => {
    if (!sellerTerms || !termsAgreed) return;
    await api.acceptSellerTerms(sellerTerms.id);
    loadMeta();
  };

  if (!accessChecked || !canSell) {
    return <PageShell><div style={s('color:var(--bs-text-muted);padding:40px 0')}>Loading…</div></PageShell>;
  }

  return (
    <PageShell>
      <ShopTabs active="sell" />
      <div style={s('font-size:11.5px;font-weight:600;letter-spacing:0.15em;color:var(--bs-text-label);margin-bottom:8px')}>SELL YOUR BEATS</div>
      <h1 style={s('margin:0 0 26px;font-weight:700;font-size:40px;letter-spacing:-0.025em;line-height:1.15;color:var(--bs-text)')}>Seller Studio</h1>

      {error && <div style={s('padding:12px 14px;background:var(--bs-red-bg);border:1px solid var(--bs-red-border);border-radius:10px;color:var(--bs-red);font-size:13px;margin-bottom:18px')}>{error}</div>}

      {/* seller terms gate */}
      {meta && !meta.seller_terms_accepted ? (
        <div style={s(cardPanel + 'padding:26px;max-width:640px')}>
          <h2 style={s('margin:0 0 8px;font-weight:700;font-size:22px')}>Seller Agreement</h2>
          <p style={s('margin:0 0 18px;color:var(--bs-text-muted);font-size:13.5px;line-height:1.6')}>
            Before your first listing, accept Nova's seller terms — you attest you own every uploaded work outright (no uncleared samples), and agree to the exclusive-sale model: sale price minus platform commission, plus 5% of the buyer's record distribution revenue paid to you.
          </p>
          <div style={s('max-height:220px;overflow:auto;background:var(--bs-input);border:1px solid var(--bs-border);border-radius:12px;padding:16px;font-size:12.5px;color:var(--bs-text-body);line-height:1.6;white-space:pre-wrap;margin-bottom:16px')}>
            {sellerTerms?.body_md || 'Loading terms…'}
          </div>
          <button onClick={() => setTermsAgreed(!termsAgreed)} style={s('display:flex;gap:11px;align-items:center;width:100%;text-align:left;background:var(--bs-input);border:1px solid var(--bs-border);border-radius:12px;padding:14px;cursor:pointer;margin-bottom:16px')}>
            <div style={s('width:20px;height:20px;border-radius:6px;flex-shrink:0;display:flex;align-items:center;justify-content:center;' + (termsAgreed ? 'background:#2F6BFF;border:1px solid #2F6BFF' : 'background:transparent;border:1.5px solid var(--bs-border-3)'))}>
              {termsAgreed && <svg width="11" height="11" viewBox="0 0 14 14" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 7.5l3 3 6-7" /></svg>}
            </div>
            <span style={s('font-size:12.5px;color:var(--bs-text-muted)')}>I am the sole rights holder of the works I upload and agree to the Seller Agreement{sellerTerms ? ` (v${sellerTerms.version})` : ''}.</span>
          </button>
          <PrimaryBtn onClick={acceptTerms} disabled={!termsAgreed}>Accept &amp; start selling</PrimaryBtn>
        </div>
      ) : (
        <div>
          <div className="bs-seller-tabs" style={s('display:flex;background:var(--bs-surface);border:1px solid var(--bs-border);border-radius:9px;padding:3px;gap:2px;width:fit-content;margin-bottom:24px')}>
            {(['listings', 'upload', 'sales'] as Tab[]).map((tb) => (
              <button key={tb} onClick={() => setTab(tb)} style={s(tab === tb ? segOn : segOff)}>
                {tb === 'listings' ? 'My Listings' : tb === 'upload' ? '+ Upload Beat' : 'Sales & Earnings'}
              </button>
            ))}
          </div>

          {tab === 'listings' && <ListingsTab beats={beats} meta={meta} onChanged={loadBeats} onUploadClick={() => setTab('upload')} />}
          {tab === 'upload' && meta && <UploadTab meta={meta} onDone={() => { loadBeats(); setTab('listings'); }} />}
          {tab === 'sales' && <SalesTab sales={sales} royalties={royalties} />}
        </div>
      )}
    </PageShell>
  );
};

/** The pipeline stores failures on the draft as "Media pipeline failed: <reason>". */
const pipelineError = (b: SellerBeat): string | null =>
  b.status === 'draft' && b.rejection_reason?.startsWith('Media pipeline failed:')
    ? b.rejection_reason.replace('Media pipeline failed:', '').trim() : null;

const ListingsTab: React.FC<{ beats: SellerBeat[] | null; meta: SellerMeta | null; onChanged: () => void; onUploadClick: () => void }> = ({ beats, meta, onChanged, onUploadClick }) => {
  const [busy, setBusy] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [finishing, setFinishing] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [editingListed, setEditingListed] = useState<string | null>(null);

  const act = async (fn: () => Promise<any>, id: string) => {
    setBusy(id);
    setRowError(null);
    try { await fn(); onChanged(); } catch (e: any) { setRowError({ id, message: e.message }); } finally { setBusy(null); }
  };

  // while anything is processing, keep the list fresh so the state resolves on-screen
  const anyProcessing = !!beats?.some((b) => b.status === 'processing' && !b.processing_stale);
  useEffect(() => {
    if (!anyProcessing) return undefined;
    const t = setInterval(onChanged, 5000);
    return () => clearInterval(t);
  }, [anyProcessing, onChanged]);

  if (beats === null) return <div style={s('color:var(--bs-text-muted)')}>Loading…</div>;
  if (beats.length === 0) {
    return (
      <div style={s('display:flex;flex-direction:column;align-items:center;padding:70px 20px;text-align:center;border:1px dashed var(--bs-border-2);border-radius:18px;background:var(--bs-well)')}>
        <div style={s('font-weight:700;font-size:19px;color:var(--bs-text-hi)')}>No listings yet</div>
        <p style={s('margin:9px 0 20px;color:var(--bs-text-muted);font-size:13.5px;max-width:340px;line-height:1.5')}>Upload a beat — once approved it goes live in the Library as a one-of-one exclusive.</p>
        <PrimaryBtn onClick={onUploadClick}>Upload your first beat</PrimaryBtn>
      </div>
    );
  }

  const deletable = (b: SellerBeat) => b.status === 'draft' || b.status === 'rejected';
  const deleteBtn = (b: SellerBeat) => (
    confirmDelete === b.id ? (
      <GhostBtn onClick={() => { setConfirmDelete(null); act(() => api.deleteBeat(b.id), b.id); }}
        style="padding:9px 15px;font-size:13px;color:var(--bs-red);border-color:var(--bs-red-border)">
        {busy === b.id ? '…' : 'Really delete?'}
      </GhostBtn>
    ) : (
      <GhostBtn onClick={() => { setConfirmDelete(b.id); setTimeout(() => setConfirmDelete((c) => (c === b.id ? null : c)), 3500); }}
        style="padding:9px 15px;font-size:13px">
        Delete
      </GhostBtn>
    )
  );

  return (
    <div>
      {beats.map((b) => {
        const failure = pipelineError(b);
        const uploadIncomplete = b.status === 'draft' && !b.media_ready && b.all_required_files_uploaded === false;
        const readyToProcess = b.status === 'draft' && !b.media_ready && b.all_required_files_uploaded === true;
        return (
          <div key={b.id} style={s('border-bottom:1px solid var(--bs-line)')}>
            <div style={s('display:flex;align-items:center;gap:14px;padding:14px 10px')}>
              <BeatThumb />
              <div style={s('flex:1;min-width:0')}>
                <div style={s('display:flex;align-items:center;gap:10px')}>
                  <span style={s('font-weight:600;font-size:15px;color:var(--bs-text)')}>{b.name}</span>
                  <StatusChip status={b.status} />
                </div>
                <div style={s('font-size:12px;color:var(--bs-text-label);margin-top:3px')}>
                  {b.bpm} BPM · {b.musical_key} · {b.genres.join(', ')} · {b.stem_count} stems · {fmtPoints(b.price_points)} / {fmtCents(b.price_cents)}
                </div>
                {b.status === 'rejected' && b.rejection_reason && (
                  <div style={s('font-size:12px;color:var(--bs-red);margin-top:4px')}>Rejected: {b.rejection_reason}</div>
                )}
                {b.has_pending_edit && (
                  <div style={s('font-size:12px;color:var(--bs-blue-soft);margin-top:4px')}>
                    Edit pending admin review — listing stays live on the current version. Wait for approve/reject before editing again.
                  </div>
                )}
                {b.status === 'processing' && !b.processing_stale && (
                  <div style={s('font-size:12px;color:var(--bs-amber);margin-top:4px')}>Processing audio — validating or generating preview, MP3 and stems zip…</div>
                )}
                {b.status === 'processing' && b.processing_stale && (
                  <div style={s('font-size:12px;color:var(--bs-red);margin-top:4px')}>Processing appears to have been interrupted — you can retry.</div>
                )}
                {uploadIncomplete && (
                  <div style={s('font-size:12px;color:var(--bs-amber);margin-top:4px')}>The audio files never finished uploading — finish the upload to continue.</div>
                )}
                {failure && (
                  <div style={s('font-size:12px;color:var(--bs-red);margin-top:4px')}>Processing failed: {failure}</div>
                )}
                {rowError?.id === b.id && (
                  <div style={s('font-size:12px;color:var(--bs-red);margin-top:4px')}>{rowError.message}</div>
                )}
              </div>
              <div style={s('display:flex;gap:8px;flex-shrink:0')}>
                {b.status === 'draft' && b.media_ready && (
                  <PrimaryBtn onClick={() => act(() => api.submitBeat(b.id), b.id)} disabled={busy === b.id} style="padding:9px 15px;font-size:13px">
                    {busy === b.id ? '…' : 'Submit for review'}
                  </PrimaryBtn>
                )}
                {uploadIncomplete && (
                  <PrimaryBtn onClick={() => setFinishing(finishing === b.id ? null : b.id)} style="padding:9px 15px;font-size:13px">
                    {finishing === b.id ? 'Close' : 'Finish upload'}
                  </PrimaryBtn>
                )}
                {readyToProcess && (
                  <GhostBtn onClick={() => act(() => api.filesComplete(b.id), b.id)} style="padding:9px 15px;font-size:13px">
                    {busy === b.id ? '…' : failure ? 'Retry processing' : 'Process audio'}
                  </GhostBtn>
                )}
                {b.status === 'processing' && b.processing_stale && (
                  <GhostBtn onClick={() => act(() => api.filesComplete(b.id), b.id)} style="padding:9px 15px;font-size:13px">
                    {busy === b.id ? '…' : 'Retry processing'}
                  </GhostBtn>
                )}
                {(b.status === 'pending_review' || b.status === 'listed') && (
                  <GhostBtn onClick={() => act(() => api.withdrawBeat(b.id), b.id)} style="padding:9px 15px;font-size:13px">
                    {busy === b.id ? '…' : 'Withdraw'}
                  </GhostBtn>
                )}
                {(b.status === 'draft' || b.status === 'rejected') && meta && (
                  <GhostBtn onClick={() => setEditing(editing === b.id ? null : b.id)} style="padding:9px 15px;font-size:13px">
                    {editing === b.id ? 'Close' : 'Edit'}
                  </GhostBtn>
                )}
                {b.status === 'listed' && meta && !b.has_pending_edit && (
                  <GhostBtn onClick={() => setEditingListed(editingListed === b.id ? null : b.id)} style="padding:9px 15px;font-size:13px">
                    {editingListed === b.id ? 'Close' : 'Edit listing'}
                  </GhostBtn>
                )}
                {deletable(b) && deleteBtn(b)}
              </div>
            </div>
            {finishing === b.id && (
              <FinishUploadPanel beat={b} onDone={() => { setFinishing(null); onChanged(); }} />
            )}
            {editing === b.id && meta && (
              <EditBeatPanel beat={b} meta={meta} onDone={() => { setEditing(null); onChanged(); }} />
            )}
            {editingListed === b.id && meta && (
              <EditListedBeatPanel beat={b} meta={meta} onDone={() => { setEditingListed(null); onChanged(); }} />
            )}
          </div>
        );
      })}
    </div>
  );
};

/** Inline metadata editor for draft/rejected beats (audio files are fixed at creation). */
const EditBeatPanel: React.FC<{ beat: SellerBeat; meta: SellerMeta; onDone: () => void }> = ({ beat, meta, onDone }) => {
  const [name, setName] = useState(beat.name);
  const [bpm, setBpm] = useState(beat.bpm);
  const [musicalKey, setMusicalKey] = useState(beat.musical_key);
  const [genres, setGenres] = useState<string[]>(beat.genres);
  const [pricePoints, setPricePoints] = useState(Number(beat.price_points));
  const [priceCents, setPriceCents] = useState(Number(beat.price_cents));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleGenre = (g: string) =>
    setGenres((prev) => prev.includes(g) ? prev.filter((x) => x !== g) : prev.length < 6 ? [...prev, g] : prev);

  const save = async () => {
    setError(null);
    if (!name.trim() || genres.length === 0) { setError('Name and at least one genre are required'); return; }
    setSaving(true);
    try {
      await api.updateBeat(beat.id, {
        name: name.trim(), bpm, musical_key: musicalKey, genres,
        price_points: pricePoints, price_cents: priceCents,
      });
      onDone();
    } catch (e: any) {
      setError(e.message || 'Could not save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bs-seller-form" style={s('margin:0 10px 16px 68px;padding:18px;background:var(--bs-well);border:1px solid var(--bs-border);border-radius:14px;max-width:560px')}>
      <div style={s('display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:12px')}>
        <div style={s('grid-column:1 / -1')}>
          <label style={s(labelStyle)}>BEAT NAME</label>
          <input value={name} onChange={(e) => setName(e.target.value)} style={s(inputStyle)} maxLength={80} />
        </div>
        <div>
          <label style={s(labelStyle)}>BPM</label>
          <input type="number" value={bpm} min={40} max={300} onChange={(e) => setBpm(+e.target.value)} style={s(inputStyle)} />
        </div>
        <div>
          <label style={s(labelStyle)}>KEY</label>
          <select value={musicalKey} onChange={(e) => setMusicalKey(e.target.value)} style={s(inputStyle + ';appearance:none')}>
            {meta.keys.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
        <div>
          <label style={s(labelStyle)}>PRICE — POINTS ✦</label>
          <input type="number" value={pricePoints} min={meta.pricing_bounds.price_points_min} max={meta.pricing_bounds.price_points_max}
            onChange={(e) => setPricePoints(+e.target.value)} style={s(inputStyle)} />
        </div>
        <div>
          <label style={s(labelStyle)}>PRICE — CASH (CENTS)</label>
          <input type="number" value={priceCents} min={meta.pricing_bounds.price_cents_min} max={meta.pricing_bounds.price_cents_max}
            onChange={(e) => setPriceCents(+e.target.value)} style={s(inputStyle)} />
        </div>
      </div>
      <label style={s(labelStyle)}>GENRES (UP TO 6)</label>
      <div style={s('display:flex;flex-wrap:wrap;gap:7px;margin-bottom:14px')}>
        {meta.genres.map((g) => (
          <button key={g} onClick={() => toggleGenre(g)} style={s('padding:6px 12px;border-radius:999px;font-size:12px;font-weight:600;cursor:pointer;transition:all .12s;' +
            (genres.includes(g) ? 'background:rgba(47,107,255,0.16);border:1px solid rgba(47,107,255,0.5);color:var(--bs-blue-soft)' : 'background:var(--bs-surface);border:1px solid var(--bs-border-2);color:var(--bs-text-body)'))}>
            {g}
          </button>
        ))}
      </div>
      {error && <div style={s('margin-bottom:12px;color:var(--bs-red);font-size:12.5px')}>{error}</div>}
      <div style={s('display:flex;align-items:center;gap:10px')}>
        <PrimaryBtn onClick={save} disabled={saving} style="padding:9px 16px;font-size:13px">{saving ? 'Saving…' : 'Save changes'}</PrimaryBtn>
        {beat.status === 'rejected' && (
          <span style={s('color:var(--bs-text-faint);font-size:12px')}>Saving returns this beat to draft — submit it for review again when ready.</span>
        )}
      </div>
    </div>
  );
};

/** Listed-beat edit: same layout as Upload Beat; stays live until admin decides. */
const EditListedBeatPanel: React.FC<{ beat: SellerBeat; meta: SellerMeta; onDone: () => void }> = ({ beat, meta, onDone }) => {
  const [name, setName] = useState(beat.name);
  const [bpm, setBpm] = useState(beat.bpm);
  const [musicalKey, setMusicalKey] = useState(beat.musical_key);
  const [genres, setGenres] = useState<string[]>(beat.genres);
  const [pricePoints, setPricePoints] = useState(Number(beat.price_points));
  const [priceCents, setPriceCents] = useState(Number(beat.price_cents));
  const [masterFile, setMasterFile] = useState<File | null>(null);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [stemFiles, setStemFiles] = useState<{ label: string; file: File }[]>([]);
  const [stemError, setStemError] = useState<string | null>(null);
  const [phase, setPhase] = useState<'form' | 'uploading' | 'processing'>('form');
  const [progress, setProgress] = useState('');
  const [error, setError] = useState<string | null>(null);

  const toggleGenre = (g: string) =>
    setGenres((prev) => prev.includes(g) ? prev.filter((x) => x !== g) : prev.length < 6 ? [...prev, g] : prev);

  const addStems = (files: FileList | null) => {
    if (!files) return;
    if (stemFiles.length + files.length > MAX_STEMS) {
      setStemError(`You cannot upload more than ${MAX_STEMS} stems.`);
      return;
    }
    setStemError(null);
    setStemFiles([
      ...stemFiles,
      ...Array.from(files).map((f) => ({ label: f.name.replace(/\.wav$/i, '').slice(0, 60), file: f })),
    ]);
  };

  const save = async () => {
    setError(null);
    if (!name.trim() || genres.length === 0) {
      setError('Name and at least one genre are required');
      return;
    }
    const hasAnyFile = !!(masterFile || previewFile || stemFiles.length);
    if (hasAnyFile) {
      if (!masterFile || stemFiles.length < 1) {
        setError('If you upload any music file, provide master and stems (preview is optional). Partial audio uploads are not allowed.');
        return;
      }
      const previewErr = await validatePreviewFile(previewFile);
      if (previewErr) { setError(previewErr); return; }
    }
    try {
      if (!hasAnyFile) {
        setPhase('uploading');
        setProgress('Submitting metadata for review…');
        await api.createListedEdit(beat.id, {
          name: name.trim(), bpm, musical_key: musicalKey, genres,
          price_points: pricePoints, price_cents: priceCents,
          replace_audio: false,
          stems: [],
        });
        onDone();
        return;
      }

      setPhase('uploading');
      setProgress('Creating pending edit…');
      const { edit, uploads } = await api.createListedEdit(beat.id, {
        name: name.trim(), bpm, musical_key: musicalKey, genres,
        price_points: pricePoints, price_cents: priceCents,
        replace_audio: true,
        stems: stemFiles.map((sf) => sf.label),
      });
      const masterSlot = uploads.find((u) => u.kind === 'master_wav')!;
      setProgress('Uploading master…');
      await api.uploadFile(masterSlot.upload_url, masterFile!, masterSlot.content_type);
      const previewSlot = uploads.find((u) => u.kind === 'preview_clip');
      if (previewFile && previewSlot) {
        setProgress('Uploading preview…');
        await api.uploadFile(previewSlot.upload_url, previewFile, previewSlot.content_type);
      }
      for (const sf of stemFiles) {
        const slot = uploads.find((u) => u.kind === 'stem' && u.label === sf.label);
        if (slot) {
          setProgress(`Uploading stem: ${sf.label}…`);
          await api.uploadFile(slot.upload_url, sf.file, slot.content_type);
        }
      }
      setPhase('processing');
      setProgress(previewFile
        ? 'Validating preview and processing MP3/stems zip…'
        : 'Processing audio (generating preview, MP3, stems zip)…');
      await api.listedEditFilesComplete(beat.id, edit.id);
      onDone();
    } catch (e: any) {
      setError(e.message || 'Could not submit listed edit');
      setPhase('form');
    }
  };

  if (phase !== 'form') {
    return (
      <div style={s('margin:0 10px 16px 68px;' + cardPanel + 'padding:40px;text-align:center;max-width:640px')}>
        <div style={s('font-weight:700;font-size:19px;margin-bottom:10px')}>{phase === 'uploading' ? 'Uploading…' : 'Processing…'}</div>
        <div style={s('color:var(--bs-text-muted);font-size:13.5px')}>{progress}</div>
        <UploadInProgressNotice />
      </div>
    );
  }

  return (
    <div className="bs-seller-form" style={s('margin:0 10px 16px 68px;' + cardPanel + 'padding:26px;max-width:640px')}>
      <div style={s('font-size:12.5px;color:var(--bs-text-muted);margin-bottom:16px;line-height:1.45')}>
        Submit changes for admin review. Your current listing stays live until they approve or reject.
        Music files are optional — leave them empty to change metadata only. If you upload any music file, you must provide master and stems (preview optional); partial audio is not allowed.
      </div>

      <div style={s('display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:16px')}>
        <div style={s('grid-column:1 / -1')}>
          <label style={s(labelStyle)}>BEAT NAME</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Midnight Orbit" style={s(inputStyle)} maxLength={80} />
        </div>
        <div>
          <label style={s(labelStyle)}>BPM</label>
          <input type="number" value={bpm} min={40} max={300} onChange={(e) => setBpm(+e.target.value)} style={s(inputStyle)} />
        </div>
        <div>
          <label style={s(labelStyle)}>KEY</label>
          <select value={musicalKey} onChange={(e) => setMusicalKey(e.target.value)} style={s(inputStyle + ';appearance:none')}>
            {meta.keys.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
      </div>

      <label style={s(labelStyle)}>GENRES (UP TO 6)</label>
      <div style={s('display:flex;flex-wrap:wrap;gap:8px;margin-bottom:18px')}>
        {meta.genres.map((g) => (
          <button key={g} type="button" onClick={() => toggleGenre(g)} style={s('padding:7px 13px;border-radius:999px;font-size:12.5px;font-weight:600;cursor:pointer;transition:all .12s;' +
            (genres.includes(g) ? 'background:rgba(47,107,255,0.16);border:1px solid rgba(47,107,255,0.5);color:var(--bs-blue-soft)' : 'background:var(--bs-surface);border:1px solid var(--bs-border-2);color:var(--bs-text-body)'))}>
            {g}
          </button>
        ))}
      </div>

      <div style={s('display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:18px')}>
        <div>
          <label style={s(labelStyle)}>PRICE — NOVA POINTS ✦</label>
          <input type="number" value={pricePoints} min={meta.pricing_bounds.price_points_min} max={meta.pricing_bounds.price_points_max}
            onChange={(e) => setPricePoints(+e.target.value)} style={s(inputStyle)} />
          <div style={s('font-size:11px;color:var(--bs-text-faint);margin-top:5px')}>{meta.pricing_bounds.price_points_min.toLocaleString()}–{meta.pricing_bounds.price_points_max.toLocaleString()}</div>
        </div>
        <div>
          <label style={s(labelStyle)}>PRICE — CASH (CENTS)</label>
          <input type="number" value={priceCents} min={meta.pricing_bounds.price_cents_min} max={meta.pricing_bounds.price_cents_max}
            onChange={(e) => setPriceCents(+e.target.value)} style={s(inputStyle)} />
          <div style={s('font-size:11px;color:var(--bs-text-faint);margin-top:5px')}>= {fmtCents(priceCents)}</div>
        </div>
      </div>

      <div style={s('display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:20px')}>
        <div>
          <label style={s(labelStyle)}>MASTER (WAV, 48kHz)</label>
          <label
            {...fileDropProps((files) => setMasterFile(files[0] || null))}
            style={s('display:block;padding:22px;border:1px dashed var(--bs-border-3);border-radius:12px;text-align:center;cursor:pointer;color:var(--bs-text-muted);font-size:13px')}
          >
            {masterFile ? masterFile.name : 'Choose or drop master WAV'}
            <input type="file" accept=".wav,audio/wav" style={{ display: 'none' }} onChange={(e) => setMasterFile(e.target.files?.[0] || null)} />
          </label>
        </div>
        <div>
          <label style={s(labelStyle)}>PREVIEW (MP3, OPTIONAL)</label>
          <label
            {...fileDropProps(async (files) => {
              const f = files[0] || null;
              if (!f) {
                setPreviewFile(null);
                return;
              }
              const err = await validatePreviewFile(f);
              if (err) {
                setPreviewFile(null);
                setError(err);
                return;
              }
              setError(null);
              setPreviewFile(f);
            })}
            style={s('display:block;padding:22px;border:1px dashed var(--bs-border-3);border-radius:12px;text-align:center;cursor:pointer;color:var(--bs-text-muted);font-size:13px')}
          >
            {previewFile ? previewFile.name : 'Choose or drop preview MP3'}
            <input
              type="file"
              accept=".mp3,audio/mpeg"
              style={{ display: 'none' }}
              onChange={async (e) => {
                const f = e.target.files?.[0] || null;
                e.currentTarget.value = '';
                if (!f) {
                  setPreviewFile(null);
                  return;
                }
                const err = await validatePreviewFile(f);
                if (err) {
                  setPreviewFile(null);
                  setError(err);
                  return;
                }
                setError(null);
                setPreviewFile(f);
              }}
            />
          </label>
          <div style={s('font-size:11px;color:var(--bs-text-faint);margin-top:5px')}>
            Optional · MP3 · max 10 MB. If you skip it, Nova generates a 45s preview from the master. Shop playback still stops at 45s.
          </div>
          {previewFile && (
            <button type="button" onClick={() => { setPreviewFile(null); setError(null); }} style={s('margin-top:6px;background:none;border:none;padding:0;color:var(--bs-blue-soft);font-size:11.5px;cursor:pointer')}>Remove preview</button>
          )}
        </div>
        <div style={s('grid-column:1 / -1')}>
          <label style={s(labelStyle)}>STEMS (WAV, UP TO {MAX_STEMS})</label>
          <label
            {...fileDropProps((files) => addStems(files))}
            style={s('display:block;padding:22px;border:1px dashed var(--bs-border-3);border-radius:12px;text-align:center;cursor:pointer;color:var(--bs-text-muted);font-size:13px')}
          >
            {stemFiles.length ? `${stemFiles.length} stem(s) selected` : 'Add or drop stem files'}
            <input type="file" accept=".wav,audio/wav" multiple style={{ display: 'none' }} onChange={(e) => { addStems(e.target.files); e.currentTarget.value = ''; }} />
          </label>
          {stemError && <div style={s('margin-top:6px;color:var(--bs-red);font-size:12.5px')}>{stemError}</div>}
          {stemFiles.length > 0 && (
            <div style={s('margin-top:8px;display:flex;flex-direction:column;gap:4px')}>
              {stemFiles.map((sf, i) => (
                <div key={i} style={s('display:flex;align-items:center;justify-content:space-between;font-size:12px;color:var(--bs-text-body)')}>
                  <span>{sf.label}</span>
                  <button type="button" onClick={() => { setStemFiles(stemFiles.filter((_, j) => j !== i)); setStemError(null); }} style={s('background:none;border:none;color:var(--bs-text-faint);cursor:pointer')}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {error && <div style={s('padding:11px 13px;background:var(--bs-red-bg);border:1px solid var(--bs-red-border);border-radius:10px;color:var(--bs-red);font-size:13px;margin-bottom:16px')}>{error}</div>}

      <PrimaryBtn onClick={save} style="width:100%;padding:14px">Submit edit for review</PrimaryBtn>
      <div style={s('text-align:center;margin-top:10px;font-size:11.5px;color:var(--bs-text-faint)')}>
        Edits go to admin review; your current listing stays live until they decide.
      </div>
    </div>
  );
};

/** Inline panel to complete a draft whose files never reached storage. */
const FinishUploadPanel: React.FC<{ beat: SellerBeat; onDone: () => void }> = ({ beat, onDone }) => {
  const [slots, setSlots] = useState<UploadSlot[] | null>(null);
  const [files, setFiles] = useState<Record<string, File>>({});
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getUploadUrls(beat.id).then((r) => setSlots(r.uploads)).catch((e) => setError(e.message));
  }, [beat.id]);

  const slotKey = (u: UploadSlot) => `${u.kind}:${u.label || ''}`;
  const missing = (slots || []).filter((u) => !u.optional && !u.uploaded && !files[slotKey(u)]);

  const start = async () => {
    if (!slots) return;
    setError(null);
    try {
      const previewSlot = slots.find((u) => u.kind === 'preview_clip');
      const previewPick = previewSlot ? files[slotKey(previewSlot)] : undefined;
      const previewErr = await validatePreviewFile(previewPick);
      if (previewErr) {
        setError(previewErr);
        return;
      }
      for (const u of slots) {
        const f = files[slotKey(u)];
        if (!f) continue;
        const uploadLabel = u.kind === 'master_wav' ? 'master'
          : u.kind === 'preview_clip' ? 'preview' : `stem: ${u.label}`;
        setProgress(`Uploading ${uploadLabel}…`);
        await api.uploadFile(u.upload_url, f, u.content_type);
      }
      setProgress(previewPick
        ? 'Validating preview and processing MP3/stems zip…'
        : 'Processing audio (generating preview, MP3, stems zip)…');
      await api.filesComplete(beat.id);
      onDone();
    } catch (e: any) {
      setError(e.message || 'Upload failed');
      setProgress(null);
    }
  };

  return (
    <div style={s('margin:0 10px 16px 68px;padding:16px;background:var(--bs-well);border:1px solid var(--bs-border);border-radius:14px')}>
      {!slots && !error && <div style={s('color:var(--bs-text-muted);font-size:13px')}>Preparing upload…</div>}
      {slots && (
        <div style={s('display:flex;flex-direction:column;gap:8px')}>
          {slots.map((u) => (
            <div key={slotKey(u)} style={s('display:flex;align-items:center;gap:12px;font-size:13px')}>
              <span style={s('min-width:180px;color:var(--bs-text-body)')}>
                {u.kind === 'master_wav' ? 'Master WAV'
                  : u.kind === 'preview_clip' ? 'Preview MP3 (optional)' : `Stem — ${u.label}`}
              </span>
              {u.uploaded && !files[slotKey(u)] ? (
                <span style={s('color:var(--bs-green);font-size:12.5px')}>✓ already uploaded</span>
              ) : (
                <div>
                  <label
                    {...fileDropProps(async (dropped) => {
                      const f = dropped[0];
                      if (!f) return;
                      if (u.kind === 'preview_clip') {
                        const err = await validatePreviewFile(f);
                        if (err) {
                          setError(err);
                          return;
                        }
                      }
                      setError(null);
                      setFiles((prev) => ({ ...prev, [slotKey(u)]: f }));
                    })}
                    style={s('cursor:pointer;color:var(--bs-blue-soft);font-size:12.5px')}
                  >
                    {files[slotKey(u)] ? files[slotKey(u)].name
                      : u.kind === 'preview_clip' ? 'Choose or drop MP3…' : 'Choose or drop WAV…'}
                    <input type="file" accept={u.kind === 'preview_clip' ? '.mp3,audio/mpeg' : '.wav,audio/wav'} style={{ display: 'none' }}
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        if (u.kind === 'preview_clip') {
                          const err = await validatePreviewFile(f);
                          if (err) {
                            setError(err);
                            e.currentTarget.value = '';
                            return;
                          }
                        }
                        setError(null);
                        setFiles((prev) => ({ ...prev, [slotKey(u)]: f }));
                      }} />
                  </label>
                  {!u.optional && !u.uploaded && !files[slotKey(u)] && (
                    <div style={s('margin-top:3px;color:var(--bs-text-muted);font-size:11.5px;line-height:1.4')}>
                      The previously selected file for “{u.kind === 'master_wav' ? 'Master WAV' : u.label}” was not found in storage. Please select and upload it again.
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          <div style={s('display:flex;align-items:center;gap:12px;margin-top:8px')}>
            <PrimaryBtn onClick={start} disabled={missing.length > 0 || !!progress} style="padding:9px 15px;font-size:13px">
              {progress ? '…' : 'Upload & process'}
            </PrimaryBtn>
            {missing.length > 0 && !progress && (
              <span style={s('color:var(--bs-text-faint);font-size:12px')}>{missing.length} file(s) still needed</span>
            )}
            {progress && <span style={s('color:var(--bs-text-muted);font-size:12.5px')}>{progress}</span>}
          </div>
          {progress && <UploadInProgressNotice />}
        </div>
      )}
      {error && <div style={s('margin-top:10px;color:var(--bs-red);font-size:12.5px')}>{error}</div>}
    </div>
  );
};

const MAX_STEMS = 25;

const UploadTab: React.FC<{ meta: SellerMeta; onDone: () => void }> = ({ meta, onDone }) => {
  const [name, setName] = useState('');
  const [bpm, setBpm] = useState(120);
  const [musicalKey, setMusicalKey] = useState(meta.keys[0] || 'A minor');
  const [genres, setGenres] = useState<string[]>([]);
  const [pricePoints, setPricePoints] = useState(25000);
  const [priceCents, setPriceCents] = useState(25000);
  const [masterFile, setMasterFile] = useState<File | null>(null);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [stemFiles, setStemFiles] = useState<{ label: string; file: File }[]>([]);
  const [stemError, setStemError] = useState<string | null>(null);
  const [phase, setPhase] = useState<'form' | 'uploading' | 'processing'>('form');
  const [progress, setProgress] = useState('');
  const [error, setError] = useState<string | null>(null);
  // survives a failed attempt so retries reuse the same draft instead of creating duplicates
  const draftRef = useRef<string | null>(null);

  const toggleGenre = (g: string) =>
    setGenres((prev) => prev.includes(g) ? prev.filter((x) => x !== g) : prev.length < 6 ? [...prev, g] : prev);

  const addStems = (files: FileList | null) => {
    if (!files) return;
    if (stemFiles.length + files.length > MAX_STEMS) {
      setStemError(`You cannot upload more than ${MAX_STEMS} stems.`);
      return;
    }
    setStemError(null);
    setStemFiles([
      ...stemFiles,
      ...Array.from(files).map((f) => ({ label: f.name.replace(/\.wav$/i, '').slice(0, 60), file: f })),
    ]);
  };

  const submit = async () => {
    setError(null);
    if (!name.trim() || !masterFile || genres.length === 0) {
      setError('Name, at least one genre, and a master WAV are required'); return;
    }
    const previewErr = await validatePreviewFile(previewFile);
    if (previewErr) {
      setError(previewErr);
      return;
    }
    try {
      setPhase('uploading');
      let beatId = draftRef.current;
      let uploads;
      if (beatId) {
        // a previous attempt already created the draft — reuse it with fresh URLs
        setProgress('Resuming upload…');
        await api.updateBeat(beatId, {
          name: name.trim(), bpm, musical_key: musicalKey, genres,
          price_points: pricePoints, price_cents: priceCents,
        });
        uploads = (await api.getUploadUrls(beatId)).uploads;
      } else {
        setProgress('Creating listing…');
        const created = await api.createBeat({
          name: name.trim(), bpm, musical_key: musicalKey, genres,
          price_points: pricePoints, price_cents: priceCents,
          stems: stemFiles.map((sf) => sf.label),
        });
        beatId = created.beat.id;
        draftRef.current = beatId;
        uploads = created.uploads;
      }
      const masterSlot = uploads.find((u) => u.kind === 'master_wav')!;
      setProgress('Uploading master…');
      await api.uploadFile(masterSlot.upload_url, masterFile, masterSlot.content_type);
      const previewSlot = uploads.find((u) => u.kind === 'preview_clip');
      if (previewFile && previewSlot) {
        setProgress('Uploading preview…');
        await api.uploadFile(previewSlot.upload_url, previewFile, previewSlot.content_type);
      }
      for (const sf of stemFiles) {
        const slot = uploads.find((u) => u.kind === 'stem' && u.label === sf.label);
        if (slot) {
          setProgress(`Uploading stem: ${sf.label}…`);
          await api.uploadFile(slot.upload_url, sf.file, slot.content_type);
        }
      }
      setPhase('processing');
      setProgress(previewFile
        ? 'Validating preview and processing MP3/stems zip…'
        : 'Processing audio (generating preview, MP3, stems zip)…');
      await api.filesComplete(beatId);
      draftRef.current = null;
      onDone();
    } catch (e: any) {
      setError((e.message || 'Upload failed') + (draftRef.current ? ' — retrying continues this draft, no duplicate is created' : ''));
      setPhase('form');
    }
  };

  if (phase !== 'form') {
    return (
      <div style={s(cardPanel + 'padding:40px;text-align:center;max-width:520px')}>
        <div style={s('font-weight:700;font-size:19px;margin-bottom:10px')}>{phase === 'uploading' ? 'Uploading…' : 'Processing…'}</div>
        <div style={s('color:var(--bs-text-muted);font-size:13.5px')}>{progress}</div>
        <UploadInProgressNotice />
      </div>
    );
  }

  return (
    <div style={s(cardPanel + 'padding:26px;max-width:640px')}>
      <div style={s('display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:16px')}>
        <div style={s('grid-column:1 / -1')}>
          <label style={s(labelStyle)}>BEAT NAME</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Midnight Orbit" style={s(inputStyle)} maxLength={80} />
        </div>
        <div>
          <label style={s(labelStyle)}>BPM</label>
          <input type="number" value={bpm} min={40} max={300} onChange={(e) => setBpm(+e.target.value)} style={s(inputStyle)} />
        </div>
        <div>
          <label style={s(labelStyle)}>KEY</label>
          <select value={musicalKey} onChange={(e) => setMusicalKey(e.target.value)} style={s(inputStyle + ';appearance:none')}>
            {meta.keys.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
      </div>

      <label style={s(labelStyle)}>GENRES (UP TO 6)</label>
      <div style={s('display:flex;flex-wrap:wrap;gap:8px;margin-bottom:18px')}>
        {meta.genres.map((g) => (
          <button key={g} onClick={() => toggleGenre(g)} style={s('padding:7px 13px;border-radius:999px;font-size:12.5px;font-weight:600;cursor:pointer;transition:all .12s;' +
            (genres.includes(g) ? 'background:rgba(47,107,255,0.16);border:1px solid rgba(47,107,255,0.5);color:var(--bs-blue-soft)' : 'background:var(--bs-surface);border:1px solid var(--bs-border-2);color:var(--bs-text-body)'))}>
            {g}
          </button>
        ))}
      </div>

      <div style={s('display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:18px')}>
        <div>
          <label style={s(labelStyle)}>PRICE — NOVA POINTS ✦</label>
          <input type="number" value={pricePoints} min={meta.pricing_bounds.price_points_min} max={meta.pricing_bounds.price_points_max}
            onChange={(e) => setPricePoints(+e.target.value)} style={s(inputStyle)} />
          <div style={s('font-size:11px;color:var(--bs-text-faint);margin-top:5px')}>{meta.pricing_bounds.price_points_min.toLocaleString()}–{meta.pricing_bounds.price_points_max.toLocaleString()}</div>
        </div>
        <div>
          <label style={s(labelStyle)}>PRICE — CASH (CENTS)</label>
          <input type="number" value={priceCents} min={meta.pricing_bounds.price_cents_min} max={meta.pricing_bounds.price_cents_max}
            onChange={(e) => setPriceCents(+e.target.value)} style={s(inputStyle)} />
          <div style={s('font-size:11px;color:var(--bs-text-faint);margin-top:5px')}>= {fmtCents(priceCents)}</div>
        </div>
      </div>

      <div style={s('display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:20px')}>
        <div>
          <label style={s(labelStyle)}>MASTER (WAV, 48kHz)</label>
          <label
            {...fileDropProps((files) => setMasterFile(files[0] || null))}
            style={s('display:block;padding:22px;border:1px dashed var(--bs-border-3);border-radius:12px;text-align:center;cursor:pointer;color:var(--bs-text-muted);font-size:13px')}
          >
            {masterFile ? masterFile.name : 'Choose or drop master WAV'}
            <input type="file" accept=".wav,audio/wav" style={{ display: 'none' }} onChange={(e) => setMasterFile(e.target.files?.[0] || null)} />
          </label>
        </div>
        <div>
          <label style={s(labelStyle)}>PREVIEW (MP3, OPTIONAL)</label>
          <label
            {...fileDropProps(async (files) => {
              const f = files[0] || null;
              if (!f) {
                setPreviewFile(null);
                return;
              }
              const err = await validatePreviewFile(f);
              if (err) {
                setPreviewFile(null);
                setError(err);
                return;
              }
              setError(null);
              setPreviewFile(f);
            })}
            style={s('display:block;padding:22px;border:1px dashed var(--bs-border-3);border-radius:12px;text-align:center;cursor:pointer;color:var(--bs-text-muted);font-size:13px')}
          >
            {previewFile ? previewFile.name : 'Choose or drop preview MP3'}
            <input
              type="file"
              accept=".mp3,audio/mpeg"
              style={{ display: 'none' }}
              onChange={async (e) => {
                const f = e.target.files?.[0] || null;
                e.currentTarget.value = '';
                if (!f) {
                  setPreviewFile(null);
                  return;
                }
                const err = await validatePreviewFile(f);
                if (err) {
                  setPreviewFile(null);
                  setError(err);
                  return;
                }
                setError(null);
                setPreviewFile(f);
              }}
            />
          </label>
          <div style={s('font-size:11px;color:var(--bs-text-faint);margin-top:5px')}>
            Optional · MP3 · max 10 MB. If you skip it, Nova generates a 45s preview from the master. Shop playback still stops at 45s.
          </div>
          {previewFile && (
            <button onClick={() => { setPreviewFile(null); setError(null); }} style={s('margin-top:6px;background:none;border:none;padding:0;color:var(--bs-blue-soft);font-size:11.5px;cursor:pointer')}>Remove preview</button>
          )}
        </div>
        <div style={s('grid-column:1 / -1')}>
          <label style={s(labelStyle)}>STEMS (WAV, UP TO {MAX_STEMS})</label>
          <label
            {...fileDropProps((files) => addStems(files))}
            style={s('display:block;padding:22px;border:1px dashed var(--bs-border-3);border-radius:12px;text-align:center;cursor:pointer;color:var(--bs-text-muted);font-size:13px')}
          >
            {stemFiles.length ? `${stemFiles.length} stem(s) selected` : 'Add or drop stem files'}
            <input type="file" accept=".wav,audio/wav" multiple style={{ display: 'none' }} onChange={(e) => { addStems(e.target.files); e.currentTarget.value = ''; }} />
          </label>
          {stemError && <div style={s('margin-top:6px;color:var(--bs-red);font-size:12.5px')}>{stemError}</div>}
          {stemFiles.length > 0 && (
            <div style={s('margin-top:8px;display:flex;flex-direction:column;gap:4px')}>
              {stemFiles.map((sf, i) => (
                <div key={i} style={s('display:flex;align-items:center;justify-content:space-between;font-size:12px;color:var(--bs-text-body)')}>
                  <span>{sf.label}</span>
                  <button onClick={() => { setStemFiles(stemFiles.filter((_, j) => j !== i)); setStemError(null); }} style={s('background:none;border:none;color:var(--bs-text-faint);cursor:pointer')}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {error && <div style={s('padding:11px 13px;background:var(--bs-red-bg);border:1px solid var(--bs-red-border);border-radius:10px;color:var(--bs-red);font-size:13px;margin-bottom:16px')}>{error}</div>}

      <PrimaryBtn onClick={submit} style="width:100%;padding:14px">Upload &amp; process</PrimaryBtn>
      <div style={s('text-align:center;margin-top:10px;font-size:11.5px;color:var(--bs-text-faint)')}>After processing you submit for review; approved beats go live in the Library.</div>
    </div>
  );
};

const SalesTab: React.FC<{ sales: { sales: SellerSale[]; totals: any } | null; royalties: any[] | null }> = ({ sales, royalties }) => {
  if (!sales) return <div style={s('color:var(--bs-text-muted)')}>Loading…</div>;
  return (
    <div>
      <div className="bs-seller-kpis" style={s('display:grid;grid-template-columns:repeat(3,1fr);gap:16px;margin-bottom:26px')}>
        <div style={s(cardPanel + 'padding:18px 19px;border-radius:16px')}>
          <div style={s('font-size:10.5px;font-weight:600;letter-spacing:0.13em;color:var(--bs-text-label);margin-bottom:14px')}>NET EARNED — POINTS</div>
          <div style={s('font-weight:700;font-size:26px')}>{fmtPoints(Number(sales.totals?.total_net_points || 0))}</div>
        </div>
        <div style={s(cardPanel + 'padding:18px 19px;border-radius:16px')}>
          <div style={s('font-size:10.5px;font-weight:600;letter-spacing:0.13em;color:var(--bs-text-label);margin-bottom:14px')}>NET EARNED — CASH</div>
          <div style={s('font-weight:700;font-size:26px')}>{fmtCents(Number(sales.totals?.total_net_cents || 0))}</div>
        </div>
        <div style={s(cardPanel + 'padding:18px 19px;border-radius:16px')}>
          <div style={s('font-size:10.5px;font-weight:600;letter-spacing:0.13em;color:var(--bs-text-label);margin-bottom:14px')}>ROYALTY RIGHTS</div>
          <div style={s('font-weight:700;font-size:26px')}>{royalties ? royalties.filter((r) => r.status === 'active').length : '—'} <span style={s('font-size:13px;color:var(--bs-text-muted);font-weight:500')}>× 5% of record</span></div>
        </div>
      </div>

      <div className="bs-seller-sales-head" style={s('display:grid;grid-template-columns:1fr 120px 130px 130px 110px;gap:14px;padding:0 10px 12px;border-bottom:1px solid var(--bs-border)')}>
        <span style={s('font-size:11px;font-weight:600;letter-spacing:0.08em;color:var(--bs-text-label)')}>BEAT</span>
        <span className="bs-seller-col-desktop" style={s('font-size:11px;font-weight:600;letter-spacing:0.08em;color:var(--bs-text-label)')}>TENDER</span>
        <span className="bs-seller-col-desktop" style={s('font-size:11px;font-weight:600;letter-spacing:0.08em;color:var(--bs-text-label)')}>GROSS</span>
        <span style={s('font-size:11px;font-weight:600;letter-spacing:0.08em;color:var(--bs-text-label)')}>NET</span>
        <span style={s('font-size:11px;font-weight:600;letter-spacing:0.08em;color:var(--bs-text-label)')}>STATUS</span>
      </div>
      {sales.sales.length === 0 && <div style={s('padding:34px 10px;color:var(--bs-text-muted);font-size:13.5px')}>No sales yet — your listed beats appear in every artist's Library.</div>}
      {sales.sales.map((sl) => (
        <div key={sl.order_id} className="bs-seller-sales-row" style={s('display:grid;grid-template-columns:1fr 120px 130px 130px 110px;gap:14px;align-items:center;padding:13px 10px;border-bottom:1px solid var(--bs-line)')}>
          <div>
            <div style={s('font-weight:600;font-size:14px;color:var(--bs-text)')}>{sl.beat_name}</div>
            <div style={s('font-size:11.5px;color:var(--bs-text-label);margin-top:2px')}>{new Date(sl.sold_at).toLocaleDateString()}</div>
            <div className="bs-seller-row-mobile-meta">
              {sl.tender.replace('_', ' ')} · {sl.gross_points != null ? fmtPoints(Number(sl.gross_points)) : fmtCents(Number(sl.gross_cents))}
            </div>
          </div>
          <span className="bs-seller-col-desktop" style={s('font-size:12.5px;color:var(--bs-text-body)')}>{sl.tender.replace('_', ' ')}</span>
          <span className="bs-seller-col-desktop" style={s('font-size:13px;color:var(--bs-text-body);font-variant-numeric:tabular-nums')}>{sl.gross_points != null ? fmtPoints(Number(sl.gross_points)) : fmtCents(Number(sl.gross_cents))}</span>
          <span style={s('font-size:13px;color:var(--bs-green);font-weight:600;font-variant-numeric:tabular-nums')}>{sl.net_points != null ? fmtPoints(Number(sl.net_points)) : fmtCents(Number(sl.net_cents))}</span>
          <StatusChip status={sl.status} />
        </div>
      ))}
    </div>
  );
};

export default SellerPage;
